/**
 * FloorPlansList Component
 *
 * Filterable grid of floor plan cards with prominent images, pricing, and CTAs.
 * Dynamic bedroom filter pills derived from property floorplan data.
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  useContentToken,
  useCollectionBinding,
  EditableToken,
} from '@vibesites/runtime/react';
import type { ComponentContentSchema, CollectionBindingDefinition } from '@vibesites/runtime';
import type { Floorplan } from '@vibesites/schema';
import { defineComponentRequirements, collections } from '@vibesites/tokens';
// @ts-ignore
import { VibesitesImage } from '@vibesites/ui-primitives';

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {
    'floorplanslist.title': {
      label: 'Section Title',
      type: 'text',
      description: 'Main heading for the floor plans section',
      defaultValue: 'SPACIOUS FLOOR PLANS',
    },
    'floorplanslist.subtitle': {
      label: 'Subtitle',
      type: 'text',
      description: 'Subtitle beneath the main heading',
      defaultValue: 'Some of the largest apartment layouts in the area',
    },
    'floorplanslist.cta_text': {
      label: 'CTA Button Text',
      type: 'text',
      description: 'Text displayed on the call-to-action button',
      defaultValue: 'AVAILABILITY & PRICING',
    },
    'floorplanslist.disclaimer': {
      label: 'Disclaimer Text',
      type: 'text',
      description: 'Legal disclaimer text shown at the bottom',
      defaultValue: '*Availability & pricing changes daily. Please call for most up-to-date pricing & availability.',
    },
  },
  collectionBindings: {
    'floorplanslist.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
      description: 'Floor plans with bedroom count, baths, sqft, and pricing',
      allowCustomItems: true,
    } as CollectionBindingDefinition,
  },
};

export const requirements = defineComponentRequirements('FloorplansList', {
  designTokens: [
    'color.surface',
    'color.text',
    'color.textMuted',
    'color.primary',
    'color.secondary',
    'color.textOnPrimary',
    'font.heading',
    'font.body',
    'radius.md',
    'radius.lg',
    'shadow.sm',
    'shadow.lg',
  ],
  dataTokens: [
    collections.optional('floorplans', 'Floor plans'),
  ],
});

interface FloorPlanCard {
  imageUrl: string | null;
  icon: string;
  name: string;
  bedsMin: string | null;
  beds: string;
  baths: string;
  sqft: string;
  price: string;
  availableUnits: string | null;
}

function getBedroomLabel(bedsMin: string | null | undefined): string {
  if (!bedsMin || bedsMin === 'null' || bedsMin === 'false') return 'Studio';
  if (bedsMin.toLowerCase() === 'studio') return 'Studio Bed';
  const parsed = parseNumericString(bedsMin);
  if (parsed === null) return `${bedsMin} Bed`;
  if (parsed === 0) return 'Studio';
  if (parsed === 1) return '1 Bed';
  return `${parsed} Bed`;
}

function parseNumericString(value: string | null | undefined): number | null {
  if (!value || value === 'false' || value === 'null') return null;
  const normalized = value.replace(/[^0-9.]/g, '');
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatCurrency(value: number): string {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

// Format a numeric value with thousands separators and trim trailing zero
// decimals. Handles raw strings like "1300.0000" -> "1,300" and "1.5000" -> "1.5".
function formatNumber(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

// Strip trailing zero decimals from a raw string like "1.0000" -> "1".
// Falls back to the original string if it doesn't parse as a number.
function trimDecimalZeros(value: string | null | undefined): string {
  if (!value) return '';
  const parsed = parseNumericString(value);
  return parsed === null ? value : formatNumber(parsed);
}

export function FloorPlansList() {
  const sectionTitle = useContentToken('floorplanslist.title');
  const subtitle = useContentToken('floorplanslist.subtitle');
  const ctaText = useContentToken('floorplanslist.cta_text');
  const disclaimer = useContentToken('floorplanslist.disclaimer');

  const floorplans = useCollectionBinding<Floorplan>('floorplanslist.items', {
    collection: 'floorplans',
  });

  const [activeFilter, setActiveFilter] = useState('All');
  const [previewImage, setPreviewImage] = useState<{ url: string; name: string } | null>(null);

  const closePreview = useCallback(() => setPreviewImage(null), []);

  useEffect(() => {
    if (!previewImage) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePreview();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [previewImage, closePreview]);

  const isValidUrl = (url: string | null | undefined): url is string => {
    if (!url) return false;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const defaultFloorPlans: FloorPlanCard[] = [
    { imageUrl: null, icon: 'S', name: 'Studio', bedsMin: '0', beds: 'Studio', baths: '1', sqft: '515', price: '', availableUnits: null },
    { imageUrl: null, icon: '1', name: 'One Bed', bedsMin: '1', beds: '1', baths: '1', sqft: '515 - 811', price: '', availableUnits: null },
    { imageUrl: null, icon: '2', name: 'Two Beds', bedsMin: '2', beds: '2', baths: '2', sqft: '910 - 1,065', price: '', availableUnits: null },
  ];

  const formatRange = (min: string | null | undefined, max: string | null | undefined, prefix = ''): string => {
    const minNum = parseNumericString(min);
    const maxNum = parseNumericString(max);
    if (minNum === null && maxNum === null) return '';
    if (minNum === null) return `${prefix}${formatNumber(maxNum!)}`;
    if (maxNum === null) return `${prefix}${formatNumber(minNum)}`;
    if (minNum === maxNum) return `${prefix}${formatNumber(minNum)}`;
    return `${prefix}${formatNumber(minNum)} - ${prefix}${formatNumber(maxNum)}`;
  };

  const formatPriceRange = (min: string | null | undefined, max: string | null | undefined): string => {
    const minNum = parseNumericString(min);
    const maxNum = parseNumericString(max);
    if (minNum === null && maxNum === null) return '';
    if (minNum === null) return formatCurrency(maxNum!);
    if (maxNum === null) return `From ${formatCurrency(minNum)}`;
    if (minNum === maxNum) return formatCurrency(minNum);
    return `${formatCurrency(minNum)} - ${formatCurrency(maxNum)}`;
  };

  const displayPlans: FloorPlanCard[] = floorplans.length > 0
    ? floorplans.map((fp: Floorplan) => ({
        imageUrl: isValidUrl(fp.floorplan_image_url) ? fp.floorplan_image_url : null,
        icon: fp.floorplan_beds_min?.charAt(0) || fp.floorplan_name?.charAt(0) || '?',
        name: fp.floorplan_name || 'Floor Plan',
        bedsMin: fp.floorplan_beds_min ?? null,
        beds: trimDecimalZeros(fp.floorplan_beds_min) === '0' ? 'Studio' : trimDecimalZeros(fp.floorplan_beds_min),
        baths: trimDecimalZeros(fp.floorplan_baths_min) || '1',
        sqft: formatRange(fp.floorplan_sqft_min, fp.floorplan_sqft_max),
        price: formatPriceRange(fp.floorplan_price_min, fp.floorplan_price_max),
        availableUnits: fp.floorplan_available_units && fp.floorplan_available_units !== '0' && fp.floorplan_available_units !== 'null' && fp.floorplan_available_units !== 'false'
          ? fp.floorplan_available_units
          : null,
      }))
    : defaultFloorPlans;

  const lowestStartingPrice = useMemo(() => {
    // Prefer the floorplan min price so the subtitle reflects the true entry price across plans.
    const prices = floorplans
      .map((floorplan) => {
        const minPrice = parseNumericString(floorplan.floorplan_price_min);
        return minPrice ?? parseNumericString(floorplan.floorplan_price_max);
      })
      .filter((price): price is number => price !== null);

    if (prices.length === 0) return null;
    return formatCurrency(Math.min(...prices));
  }, [floorplans]);

  const subtitleText = lowestStartingPrice
    ? `${subtitle || 'Apartment Homes'} starting at ${lowestStartingPrice}`
    : subtitle;

  const bedroomCategories = useMemo(() => {
    const categoryMap = new Map<string, number>();
    displayPlans.forEach((plan) => {
      const label = getBedroomLabel(plan.bedsMin);
      categoryMap.set(label, (categoryMap.get(label) || 0) + 1);
    });

    const sorted = Array.from(categoryMap.entries()).sort((a, b) => {
      if (a[0] === 'Studio') return -1;
      if (b[0] === 'Studio') return 1;
      return parseInt(a[0]) - parseInt(b[0]);
    });

    return [
      { label: 'All', count: displayPlans.length },
      ...sorted.map(([label, count]) => ({ label, count })),
    ];
  }, [displayPlans]);

  const filteredPlans = activeFilter === 'All'
    ? displayPlans
    : displayPlans.filter((plan) => getBedroomLabel(plan.bedsMin) === activeFilter);

  return (
    <section className="bg-background py-16 md:py-24" id="floorplans" data-vibesite-slot="FloorplansList" style={{ scrollMarginTop: 'calc(var(--header-height, 64px) + var(--offer-banner-height, 0px))' }}>
      <style>{`
        @keyframes cardFadeIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes lightboxFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes lightboxScaleIn {
          from { opacity: 0; transform: scale(0.92); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>

      <div className="max-w-[1200px] mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="mb-8">
          <EditableToken path="floorplanslist.title" type="text" label="Section Title" as="div">
            <h2 className="text-[clamp(1.75rem,3vw,2.5rem)] font-bold font-heading text-foreground uppercase m-0 mb-2">
              {sectionTitle}
            </h2>
          </EditableToken>

          <EditableToken path="floorplanslist.subtitle" type="text" label="Subtitle" as="div">
            <p className="text-lg text-foreground m-0">{subtitleText}</p>
          </EditableToken>
        </div>

        {/* Bedroom filter pills */}
        {bedroomCategories.length > 2 && (
          <div className="flex flex-wrap gap-2 mb-8" role="group" aria-label="Filter by bedroom count">
            {bedroomCategories.map(({ label, count }) => (
              <button
                key={label}
                onClick={() => setActiveFilter(label)}
                aria-pressed={activeFilter === label}
                className={[
                  'px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 border-2 cursor-pointer',
                  activeFilter === label
                    ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                    : 'bg-transparent text-foreground border-border hover:border-primary hover:text-primary',
                ].join(' ')}
              >
                {label}
                <span className={[
                  'ml-1.5 text-xs font-normal',
                  activeFilter === label ? 'text-primary-foreground/70' : 'text-muted-foreground',
                ].join(' ')}>
                  ({count})
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Floor plan grid */}
        <div
          key={activeFilter}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filteredPlans.map((plan: FloorPlanCard, index: number) => (
            <div
              key={`${plan.name}-${index}`}
              className="bg-background rounded-lg shadow-sm border border-border overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col"
              style={{
                animation: 'cardFadeIn 0.35s ease-out both',
                animationDelay: `${Math.min(index * 60, 300)}ms`,
              }}
            >
              {/* Image area */}
              <div className="aspect-[4/3] bg-surface relative overflow-hidden">
                {plan.imageUrl ? (
                  <button
                    type="button"
                    onClick={() => setPreviewImage({ url: plan.imageUrl!, name: plan.name })}
                    className="w-full h-full cursor-zoom-in bg-transparent border-0 p-0 m-0 block"
                    aria-label={`View ${plan.name} floor plan full size`}
                  >
                    <VibesitesImage
                      url={plan.imageUrl}
                      alt={`${plan.name} floor plan`}
                      className="w-full h-full object-contain p-3 pointer-events-none"
                    />
                  </button>
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-surface">
                    <div className="w-20 h-20 rounded-full border-2 border-muted-foreground/20 flex items-center justify-center">
                      <span className="text-3xl font-bold text-muted-foreground/40">{plan.icon}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Content area */}
              <div className="p-5 flex flex-col flex-1 gap-2">
                {/* Plan name */}
                <h3 className="text-lg font-semibold font-heading text-foreground m-0">
                  {plan.name}
                </h3>

                {/* Specs line */}
                <p className="text-sm text-muted-foreground m-0 flex items-center gap-1.5 flex-wrap">
                  {plan.beds && (
                    <span>{plan.beds === 'Studio' ? 'Studio' : <><strong>{plan.beds}</strong> Bed</>}</span>
                  )}
                  {plan.beds && plan.baths && <span className="text-border">|</span>}
                  {plan.baths && <span><strong>{plan.baths}</strong> Bath</span>}
                  {plan.sqft && <span className="text-border">|</span>}
                  {plan.sqft && <span><strong>{plan.sqft}</strong> sqft</span>}
                </p>

                {/* Available units badge */}
                {plan.availableUnits && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 w-fit">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    {plan.availableUnits} {parseInt(plan.availableUnits) === 1 ? 'Unit' : 'Units'} Available
                  </span>
                )}

                {/* Price + CTA — always anchored to the bottom of the card */}
                <div className="mt-auto flex flex-col gap-2">
                  {plan.price && (
                    <p className="text-xl font-bold text-black m-0 pt-2">
                      {plan.price}
                    </p>
                  )}

                  {/* CTA */}
                  <EditableToken path="floorplanslist.cta_text" type="text" label="CTA Text" as="span">
                    <a
                      href="#contact"
                      className="block bg-primary text-primary-foreground py-3 px-6 rounded-md no-underline font-semibold text-sm text-center transition-all duration-200 hover:shadow-md hover:opacity-90 active:scale-[0.98]"
                    >
                      {ctaText}
                    </a>
                  </EditableToken>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Disclaimer */}
        <EditableToken
          path="floorplanslist.disclaimer"
          type="text"
          label="Disclaimer"
          as="div"
          style={{ marginTop: '2rem', textAlign: 'center' as const }}
        >
          <p className="text-sm text-muted-foreground m-0">{disclaimer}</p>
        </EditableToken>
      </div>

      {/* Image preview lightbox */}
      {previewImage && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-label={`${previewImage.name} floor plan preview`}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/80"
            onClick={closePreview}
            style={{ animation: 'lightboxFadeIn 0.2s ease-out' }}
          />

          {/* Close button */}
          <button
            type="button"
            onClick={closePreview}
            className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 text-white border-0 cursor-pointer hover:bg-black/70 transition-colors"
            aria-label="Close preview"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="4" y1="4" x2="16" y2="16" />
              <line x1="16" y1="4" x2="4" y2="16" />
            </svg>
          </button>

          {/* Image */}
          <div
            className="relative z-[1] w-[100vw] h-[100dvh] p-2 md:w-[90vw] md:h-[85vh] md:p-0 md:max-w-[1200px] flex items-center justify-center pointer-events-none"
            style={{ animation: 'lightboxScaleIn 0.2s ease-out' }}
          >
            <VibesitesImage
              url={previewImage.url}
              alt={`${previewImage.name} floor plan`}
              className="max-w-full max-h-full object-contain select-none"
            />
          </div>
        </div>
      )}
    </section>
  );
}

(FloorPlansList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;
(FloorPlansList as unknown as { requirements: typeof requirements }).requirements = requirements;

export default FloorPlansList;
